import { formatTaxonomyPathDisplayNames } from "app/pages/sales-intelligence/utils/taxonomy-tree/path";

import type {
    ImpactedAsset,
    ImpactedAssetGroup,
    ImpactReportRowsDtoKey,
    IndustryTransition,
    IndustryTransitionImpactIndustryDto,
    IndustryTransitionImpactReport,
    IndustryTransitionImpactReportDto,
    IndustryTransitionImpactRowDto,
} from "../types/impactReport";
import { impactedAssetGroupMap, impactedAssetGroupOrder } from "../types/impactReport";

/**
 * Two of the three groups happen to be spelled the same on the wire, so iterating the group map
 * against the DTO would silently yield `undefined` for saved searches. The exhaustive `Record` makes
 * a missing group a compile error and keeps the backend's `saveSearches` spelling in one literal.
 */
const dtoKeyByGroup: Record<ImpactedAssetGroup, ImpactReportRowsDtoKey> = {
    [impactedAssetGroupMap.savedSearches]: "saveSearches",
    [impactedAssetGroupMap.territories]: "territories",
    [impactedAssetGroupMap.leadScoringRules]: "leadScoringRules",
};

const TAXONOMY_PATH_SEPARATOR = "/";

const dedupe = (values: readonly string[]): readonly string[] => [...new Set(values)];

/**
 * Only the taxonomy side is re-joined. Its wire form is a fixed-depth path whose unused levels are
 * empty segments (`"Media & Entertainment//"`), which the shared formatter already trims away. Legacy
 * industry values arrive as finished display strings, so splitting them would corrupt any category name
 * that legitimately contains a slash.
 */
const toTaxonomyPaths = (values: readonly string[]): readonly string[] =>
    dedupe(values.map((value) => formatTaxonomyPathDisplayNames(value.split(TAXONOMY_PATH_SEPARATOR))).filter(Boolean));

const toIndustryTransition = (entry: IndustryTransitionImpactIndustryDto): IndustryTransition | null => {
    const industry = entry.industry.trim();

    if (!industry) {
        return null;
    }

    return {
        industry,
        taxonomyPaths: toTaxonomyPaths(entry.taxonomyPaths),
    };
};

const toIndustries = (row: IndustryTransitionImpactRowDto): readonly IndustryTransition[] =>
    row.industries
        .map(toIndustryTransition)
        .filter((transition): transition is IndustryTransition => transition !== null);

const toNewCompaniesEstimationDelta = (delta: number | null | undefined): number | undefined => {
    if (delta === null || delta === undefined || delta === 0) {
        return undefined;
    }

    return delta;
};

/** An asset with no industry filter is not impacted at all, so the backend should not have sent it. */
const isImpacted = (row: IndustryTransitionImpactRowDto): boolean => row.industries.length > 0;

export const toImpactReport = (dto: IndustryTransitionImpactReportDto): IndustryTransitionImpactReport => {
    let droppedNotImpactedCount = 0;

    const sections = impactedAssetGroupOrder.map((group) => {
        const rows = dto[dtoKeyByGroup[group]];
        const impacted = rows.filter(isImpacted);

        droppedNotImpactedCount += rows.length - impacted.length;

        const assets: readonly ImpactedAsset[] = impacted.map((row) => ({
            id: row.assetId,
            name: row.assetName,
            ownerName: row.ownerName ?? undefined,
            isAccountLevel: row.isAccountLevel ?? undefined,
            newCompaniesEstimationDelta: toNewCompaniesEstimationDelta(row.newCompaniesEstimationDelta),
            industries: toIndustries(row),
        }));

        return { group, assets };
    });

    return {
        sections,
        totalAssetCount: sections.reduce((total, section) => total + section.assets.length, 0),
        crmJobsCount: dto.crmJobsCount ?? undefined,
        droppedNotImpactedCount,
    };
};
